import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Platform, ScrollView, StyleProp, StyleSheet, TextStyle, useWindowDimensions, View, ViewProps } from 'react-native';
import useStyles from '../../hooks/styles-hook';
import useCustomTheme from '../../hooks/theme-hook';
import Label, { LabelType } from '../common/Label';
import MyAccountNavigationButton from './MyAccountNavigationButton';
import MoreInfoButton, { MoreInfoButtonProps } from '../common/MoreInfoButton';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { MyAccountRouteName } from '../../models/NavigationElements';

export interface PageWrapperButtonProps extends MoreInfoButtonProps {
  externalLink?: string;
}

export interface MyAccountPageWrapperProps extends ViewProps {
  title: string;
  titleStyle?: StyleProp<TextStyle>;
  titleAccessory?: ReactElement;
  navigationTitle: string;
  navigationAction?: MyAccountRouteName;
  navigationOnPress?: () => void;
  moreInfoComponent?: PageWrapperButtonProps;
  scrollOnChange?: boolean;
  footerElement?: ReactElement;
}

const MyAccountPageWrapper = (props: MyAccountPageWrapperProps) => {
  const theme = useCustomTheme();
  const styles = useStyles((section) => section.myAccount);
  const layout = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const panelTopInset = Math.max(insets.top, Platform.OS === 'android' ? 30 : 0);
  const footerBottomInset = Math.max(insets.bottom, Platform.OS === 'android' ? 8 : 0);

  const [scrollOnChange, setScrollOnChange] = useState(props.scrollOnChange);

  const scrollView = useRef<ScrollView | null>(null);

  useEffect(() => {
    setScrollOnChange(props.scrollOnChange);
  }, [props.scrollOnChange]);

  return (
    <View style={[styles.pageWrapperBase, layout.width > 1200 ? { width: 375, right: 0, left: 'auto' } : { paddingTop: panelTopInset }]}>
      {/* Extend the page background into the safe-area insets while keeping content layout unchanged. */}
      <View
        pointerEvents="none"
        style={[
          StyleSheet.absoluteFillObject,
          {
            top: -insets.top,
            bottom: -insets.bottom,
            backgroundColor: theme.background.primary,
          },
        ]}
      />
      <View style={styles.pageWrapper.titleBar}>
        <MyAccountNavigationButton title={props.navigationTitle} action={props.navigationAction} onPress={props.navigationOnPress} />
      </View>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        style={{ padding: 16 }}
        contentContainerStyle={{ flexGrow: 1 }}
        ref={scrollView}
        onContentSizeChange={() => (scrollOnChange ? scrollView.current.scrollToEnd({ animated: true }) : null)}
      >
        <View style={[props.titleStyle, styles.pageWrapper.title]}>
          <Label variant={LabelType.titleLargeNoCapsPrimary}>{props.title}</Label>
          {props.titleAccessory}
        </View>
        {props.children}
      </ScrollView>
      {props.footerElement && <View style={{ paddingBottom: footerBottomInset, backgroundColor: theme.background.primary }}>{props.footerElement}</View>}
      {props.moreInfoComponent && (
        <MoreInfoButton
          style={[
            styles.pageWrapper.infoButton,
            // Keep the floating help action above the iOS home indicator.
            { marginBottom: Math.max(insets.bottom, 0) + 8 },
            props.moreInfoComponent.style,
          ]}
          title={props.moreInfoComponent.title}
          showTitle={props.moreInfoComponent.showTitle}
          externalLink={props.moreInfoComponent.externalLink}
        >
          {props.moreInfoComponent.children}
        </MoreInfoButton>
      )}
    </View>
  );
};

export default MyAccountPageWrapper;
